import {
  Ban,
  Bird,
  Bomb,
  Calendar,
  CircleX,
  CloudFog,
  CloudHail,
  CloudLightning,
  CloudRainWind,
  Cross,
  ExternalLink,
  Hourglass,
  Lock,
  type LucideIcon,
  MapPin,
  MapPinOff,
  Megaphone,
  Plane,
  PlaneLanding,
  Scale,
  ShieldAlert,
  ShieldCheck,
  Siren,
  Snowflake,
  ThermometerSun,
  Tornado,
  TrafficCone,
  TriangleAlert,
  Truck,
  Users,
  UserX,
  Zap,
} from "lucide-react";

// Re-export the meta icons the card uses directly, so the component imports everything from one place.
export { Calendar, ExternalLink, MapPin, Plane, Scale };

export type CxScope = "FLIGHT" | "AIRPORT" | "COUNTRY";
export type CxConfidence = "high" | "medium" | "low" | string;

/**
 * A single detected extraordinary circumstance, as stored in a `CX` ClaimInsight's `metadata` list
 * (one entry per detected event). Mirrors the API `/insights/cx/detect` response plus the
 * airport/day the detection ran on and the jurist's `discarded` flag.
 */
export interface ExtraordinaryCircumstance {
  eventDetected: boolean;
  cxType: string;
  confidence: CxConfidence;
  /** Airport IATA the detection ran on (and the event's airport). */
  iata?: string;
  /** Airport-local date the detection ran on (`yyyy-MM-dd`). */
  date?: string;
  summary: string;
  description: string;
  startDate: string;
  endDate: string;
  sources: { title?: string; url?: string }[];
  /** Set true once a jurist rejects this detection (toggle). */
  discarded?: boolean;
}

/**
 * Per-hue Tailwind class strings, written as **complete literals** so Tailwind's content scanner picks
 * them up (dynamic `bg-${hue}-50` interpolation would be purged, and the `ring-…-600/10` opacity
 * modifier isn't covered by the project's safelist regex). Mirrors the `BadgeClaimType` pattern.
 */
const HUE_CLASSES = {
  blue: { badge: "bg-blue-50 text-blue-700 ring-blue-600/10", tile: "border-blue-200 text-blue-700" },
  red: { badge: "bg-red-50 text-red-700 ring-red-600/10", tile: "border-red-200 text-red-700" },
  indigo: {
    badge: "bg-indigo-50 text-indigo-700 ring-indigo-600/10",
    tile: "border-indigo-200 text-indigo-700",
  },
  amber: { badge: "bg-amber-50 text-amber-700 ring-amber-600/10", tile: "border-amber-200 text-amber-700" },
  violet: {
    badge: "bg-violet-50 text-violet-700 ring-violet-600/10",
    tile: "border-violet-200 text-violet-700",
  },
  yellow: {
    badge: "bg-yellow-50 text-yellow-700 ring-yellow-600/10",
    tile: "border-yellow-200 text-yellow-700",
  },
  teal: { badge: "bg-teal-50 text-teal-700 ring-teal-600/10", tile: "border-teal-200 text-teal-700" },
  zinc: { badge: "bg-zinc-50 text-zinc-700 ring-zinc-600/10", tile: "border-zinc-200 text-zinc-700" },
} as const;

type Hue = keyof typeof HUE_CLASSES;

export interface CxCategory {
  label: string;
  /** CJUE point of vigilance, rendered as a legal callout. Empty = no note. */
  note: string;
  /** Full Tailwind classes for the category badge. */
  badgeClass: string;
  /** Full Tailwind classes for the icon tile (border + text color). */
  tileClass: string;
}

const CATEGORY_DEFS: Record<number, { label: string; hue: Hue; note: string }> = {
  1: { label: "Météo extrême", hue: "blue", note: "" },
  2: { label: "Menace à la sécurité", hue: "red", note: "" },
  3: { label: "Instabilité politique / militaire", hue: "indigo", note: "" },
  4: {
    label: "Grève externe",
    hue: "amber",
    note: "Les grèves internes (personnel de la compagnie) ne sont pas des circonstances extraordinaires selon la CJUE (arrêts « Krüsemann », « Germanwings »).",
  },
  5: {
    label: "Sécurité de l'appareil",
    hue: "violet",
    note: "Les pannes techniques ordinaires, même immobilisantes, ne sont pas extraordinaires : l'entretien est une obligation prévisible du transporteur.",
  },
  6: { label: "Perturbation aéroport / espace aérien", hue: "yellow", note: "" },
  7: { label: "Restriction des autorités", hue: "indigo", note: "" },
  8: { label: "Urgence médicale", hue: "teal", note: "" },
  9: { label: "Autre cas reconnu", hue: "zinc", note: "" },
};

export const CX_CATEGORIES: Record<number, CxCategory> = Object.fromEntries(
  Object.entries(CATEGORY_DEFS).map(([id, def]) => [
    Number(id),
    {
      label: def.label,
      note: def.note,
      badgeClass: HUE_CLASSES[def.hue].badge,
      tileClass: HUE_CLASSES[def.hue].tile,
    },
  ]),
);

type CxTypeDef = [label: string, categoryId: number, scope: CxScope, icon: LucideIcon];

/** type → [label, categoryId, scope, Lucide icon]. The 30 ExtraordinaryCircumstanceTypes. */
export const CX_TYPES: Record<string, CxTypeDef> = {
  SEVERE_STORM: ["Forte tempête (vents violents, orages)", 1, "FLIGHT", CloudLightning],
  HEAVY_SNOW_OR_ICE: ["Fortes chutes de neige ou de verglas", 1, "FLIGHT", Snowflake],
  EXTREME_HEAT: ["Canicule rendant l'exploitation dangereuse", 1, "FLIGHT", ThermometerSun],
  HEAVY_RAIN_FLOODING: ["Pluies diluviennes inondant les pistes", 1, "AIRPORT", CloudRainWind],
  DENSE_FOG: ["Brouillard très dense limitant la visibilité", 1, "FLIGHT", CloudFog],
  CYCLONE: ["Cyclone", 1, "COUNTRY", Tornado],
  TERROR_THREAT: ["Attentats ou menaces terroristes", 2, "COUNTRY", ShieldAlert],
  BOMB_THREAT: ["Alerte à la bombe", 2, "AIRPORT", Bomb],
  AIRPORT_EVACUATION: ["Évacuation de l'aéroport", 2, "AIRPORT", Siren],
  POLICE_INTERVENTION: ["Intervention policière pour raisons de sécurité", 2, "AIRPORT", ShieldCheck],
  MANDATED_SECURITY_MEASURES: ["Mesures de sécurité imposées par les autorités", 2, "COUNTRY", Lock],
  AIRSPACE_CLOSURE: ["Fermeture de l'espace aérien (diplo. / militaire)", 3, "COUNTRY", Ban],
  ATC_STRIKE: ["Grève des contrôleurs aériens", 4, "COUNTRY", Megaphone],
  AIRPORT_STAFF_STRIKE: ["Grève du personnel d'aéroport", 4, "AIRPORT", Users],
  WEATHER_SERVICES_STRIKE: ["Grève des services météorologiques", 4, "COUNTRY", Megaphone],
  BIRD_STRIKE: ["Collision avec un oiseau (bird strike)", 5, "FLIGHT", Bird],
  LIGHTNING_DAMAGE: ["Foudre ayant endommagé l'appareil", 5, "FLIGHT", Zap],
  FOD: ["Objets étrangers sur la piste", 5, "AIRPORT", TriangleAlert],
  EXTERNAL_IMPACT: ["Dégâts externes (impact, débris, grêle)", 5, "FLIGHT", CloudHail],
  THIRD_PARTY_DAMAGE: ["Dommages causés par un tiers (assistance)", 5, "AIRPORT", Truck],
  RUNWAY_ACCIDENT: ["Accident sur piste ou en aérogare", 6, "AIRPORT", TriangleAlert],
  TRAFFIC_CONGESTION: ["Congestion due à des événements extérieurs", 6, "AIRPORT", TrafficCone],
  FLIGHT_RESTRICTION: ["Interdiction de survol (restrictions temporaires)", 6, "COUNTRY", Ban],
  FLIGHT_BAN_AUTHORITY: ["Restrictions de vol imposées par les autorités", 7, "COUNTRY", Ban],
  BORDER_CLOSURE: ["Fermetures de frontières", 7, "COUNTRY", MapPinOff],
  EMERGENCY_LANDING_MEDICAL: ["Atterrissage d'urgence pour raison médicale", 8, "FLIGHT", PlaneLanding],
  MEDICAL_INTERVENTION_GROUND: ["Intervention d'équipes médicales (bord / sol)", 8, "AIRPORT", Cross],
  CANCELLED_BY_AUTHORITY: ["Vols annulés à la demande des autorités", 9, "COUNTRY", CircleX],
  DISRUPTIVE_PASSENGER: ["Passager incontrôlable forçant l'atterrissage", 9, "FLIGHT", UserX],
  FORCED_WAIT_CLEARANCE: ["Attente forcée d'un feu vert des autorités", 9, "AIRPORT", Hourglass],
};

export const SCOPE_LABEL: Record<CxScope, string> = {
  FLIGHT: "Vol concerné",
  AIRPORT: "Aéroport",
  COUNTRY: "Pays / région",
};

/**
 * Visual framing driven by detection confidence (handoff §4): `high` → amber alert band; everything
 * else → neutral grey. Full literal Tailwind classes.
 */
export interface CxFraming {
  alert: boolean;
  accentFg: string;
  bandBg: string;
  bandBorder: string;
  calloutBg: string;
  calloutBorder: string;
  calloutFg: string;
}

const FRAMING_HIGH: CxFraming = {
  alert: true,
  accentFg: "text-red-700",
  bandBg: "bg-red-50",
  bandBorder: "border-red-200",
  calloutBg: "bg-red-50",
  calloutBorder: "border-red-200",
  calloutFg: "text-red-800",
};
const FRAMING_NEUTRAL: CxFraming = {
  alert: false,
  accentFg: "text-gray-600",
  bandBg: "bg-gray-50",
  bandBorder: "border-gray-200",
  calloutBg: "bg-gray-50",
  calloutBorder: "border-gray-200",
  calloutFg: "text-gray-600",
};

export function framingFor(confidence: CxConfidence): CxFraming {
  return confidence?.toLowerCase() === "high" ? FRAMING_HIGH : FRAMING_NEUTRAL;
}

const FALLBACK_TYPE: CxTypeDef = ["Circonstance extraordinaire", 9, "COUNTRY", TriangleAlert];

export interface ResolvedCircumstance {
  typeLabel: string;
  category: CxCategory;
  scope: CxScope;
  scopeLabel: string;
  icon: LucideIcon;
}

/** Resolve a `cxType` to its label, category, scope and icon. Unknown types fall back to category 9. */
export function resolveCircumstance(cxType: string): ResolvedCircumstance {
  const [typeLabel, categoryId, scope, icon] = CX_TYPES[cxType] ?? FALLBACK_TYPE;
  return {
    typeLabel,
    category: CX_CATEGORIES[categoryId] ?? CX_CATEGORIES[9],
    scope,
    scopeLabel: SCOPE_LABEL[scope],
    icon,
  };
}

const MONTHS_FR = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

function parseISODate(value?: string): { y: number; mo: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  return match ? { y: +match[1], mo: +match[2] - 1, d: +match[3] } : null;
}

/** "23 mai 2026" when start === end, else "du 23 au 24 mai 2026" / cross-month/-year variants. */
export function formatPeriod(start?: string, end?: string): string {
  const a = parseISODate(start);
  const b = parseISODate(end);
  if (!a) {
    return "—";
  }
  if (!b || start === end) {
    return `${a.d} ${MONTHS_FR[a.mo]} ${a.y}`;
  }
  if (a.y === b.y && a.mo === b.mo) {
    return `du ${a.d} au ${b.d} ${MONTHS_FR[b.mo]} ${b.y}`;
  }
  if (a.y === b.y) {
    return `du ${a.d} ${MONTHS_FR[a.mo]} au ${b.d} ${MONTHS_FR[b.mo]} ${b.y}`;
  }
  return `du ${a.d} ${MONTHS_FR[a.mo]} ${a.y} au ${b.d} ${MONTHS_FR[b.mo]} ${b.y}`;
}

export interface ParsedSource {
  title: string;
  domain: string;
  url: string;
}

/**
 * Vertex grounding URLs are opaque redirects, so the publisher lives in the title suffix (after the
 * last " - "). Splits it out; falls back to "source web" when there's no suffix.
 */
export function parseSource(source: { title?: string; url?: string }): ParsedSource {
  const rawTitle = source.title ?? "";
  const url = source.url ?? "";
  const parts = rawTitle.split(" - ");
  if (parts.length > 1) {
    const publisher = (parts.at(-1) ?? "").trim();
    const domain = `${publisher
      .toLowerCase()
      .replace(/^the\s+/, "")
      .replace(/[^a-z0-9]+/g, "")}.com`;
    return { title: parts.slice(0, -1).join(" - ").trim(), domain, url };
  }
  return { title: rawTitle, domain: "source web", url };
}
